const $ = require('lib/jquery')
const getCachedScript = require('lib/get_cached_script')
const SvgCaptchaView = require('./svg_captcha')
const config = require('lib/config')
const Raven = require('lib/raven')
const log = require('log').create('captcha')  

/*

const Captcha = require('lib/recaptcha')

Captcha.load()
    returns: a Promise which is resolved with true/false

Captcha.render(options)
    requires:
        options.element:
            a string ID, or a DOM node (preferable, e.g. this.ui.captcha.get(0))
            captcha UI will be inserted into this element
        options.onReceivedToken(token, type):
            function which will receive the captcha token when the user passes the test,
            along with the type ('recaptcha' or 'svgcaptcha'), which is used by the backend
            to validate the token
        options.onExpiredToken():
            function which will be called if the user's token expires

    optional:
        options.render_fallback:
            boolean which controls whether SVG Captcha should be shown if Google Recaptcha fails to load

    returns: the same deferred promise as .load()

*/


const Captcha = {

    PREFERRED_CAPTCHA: 'hcaptcha', // can be "recaptcha" or "hcaptcha"

    errors: {
        CAPTCHA_LOAD_FAILED: 'Unable to load Captcha UI. Do you have a blocker on?',
        CAPTCHA_TOKEN_MISSING: 'Please prove that you’re not a robot.',
        CAPTCHA_VALIDATION_FAILED: 'Uh oh! We need to make sure you’re not a robot one more time. Please try again.',
    },

    load() {
        const hcaptcha = this.PREFERRED_CAPTCHA === 'hcaptcha'

        if (! this.promise) {
            this.promise = new Promise((resolve, reject) => {
                if (hcaptcha) {
                    // load hcaptcha
                    const hcaptcha_callback_name = `hcaptchaLoaded${Date.now()}`
                    const hcaptcha_url = 'https://hcaptcha.com/1/api.js'
                    const url = `${hcaptcha_url}?onload=${hcaptcha_callback_name}&recaptchacompat=off`
                    window[hcaptcha_callback_name] = () => resolve()

                    getCachedScript(url).fail((err) => {
                        log('err', err)
                        reject(err)
                    })
                } else {
                    // the recaptcha script should already be loaded by the steps_controller
                    // (for recaptcha v3 monitoring). the recaptcha challenge uses the same script.
                    // if it's not loaded for some reason, we'll load it here.
                    this.loadRecaptcha().then(resolve).catch(reject)
                }
            })
        }

        return this.promise
    },


    loadRecaptcha() {
        return new Promise((resolve, reject) => {
            if (window.grecaptcha) {
                resolve()
            } else {
                const recaptcha_callback_name = `recaptchaV3Loaded${Date.now()}`
                window[recaptcha_callback_name] = () => {
                    log('recaptcha v3 is initialized')
                    resolve()
                }

                const recaptcha_url = config.get('google.recaptcha.url')
                const recaptcha_key = config.get('google.recaptcha.v3.key')
                const recaptcha_v3_url = `${recaptcha_url}?render=${recaptcha_key}&onload=${recaptcha_callback_name}`

                getCachedScript(recaptcha_v3_url)
                    .done((err) => {
                        log('loaded recaptcha v3 script')
                    })
                    .fail((err) => {
                        log('err', err)
                        reject(err)
                    })
            }
        })
    },


    render(options) {
        return (
            this.load()
                .then(() => this.renderRecaptcha(options))
                .catch(() => this.renderFallback(options))
                .catch((err) => Raven.captureException(err))
        )
    },

    renderRecaptcha(options) {
        const hcaptcha = this.PREFERRED_CAPTCHA === 'hcaptcha'
        const captcha_key = hcaptcha ? config.get('hcaptcha.sitekey') : config.get('google.recaptcha.key')
        const captcha_instance = window[hcaptcha ? 'hcaptcha' : 'grecaptcha']

        captcha_instance.render(options.element, {
            'sitekey': captcha_key,
            'callback': (token) => options.onReceivedToken(token, this.PREFERRED_CAPTCHA),
            'expired-callback': options.onExpiredToken,
        })

        if (options.scale) {
            this.scaleCaptcha(options)
            // doesn't always work right away
            setTimeout(() => {
                this.scaleCaptcha(options)
            }, 100)
        }
    },

    scaleCaptcha(options) {
        try {
            // adding a few px in this math to account for shadow on captcha
            const element = options.element
            const captcha = element.children[0]
            const parent_width = element.offsetWidth + 2
            const captcha_width = captcha.offsetWidth
            const captcha_height = captcha.offsetHeight + 2
            const scale = parent_width / captcha_width
            const min_height = captcha_height * scale

            $(captcha).css({
                'transform': `scale(${scale})`,
                'transform-origin': '0 0',
            })
            $(element).css({
                'min-height': `${min_height}px`,
            })
        } catch {
            // don't care
        }
    },

    renderFallback(options) {
        if (! options.render_fallback) return

        this.svg_captcha_view = new SvgCaptchaView({
            el: options.element,
        })
        this.svg_captcha_view.render().triggerMethod('attach')

        this.svg_captcha_view.captcha_text.on('change:value', (model, value) => {
            const token = value.trim()
            if (token) {
                options.onReceivedToken(token, 'svgcaptcha')
            } else {
                options.onExpiredToken()
            }
        })
    },

    destroy() {
        try {
            // don't reset this, it causes an error to be thrown in a timeout
            // window.grecaptcha.reset()
            this.svg_captcha_view.destroy()
        } catch {
            // don't care
        }
    },

}

module.exports = Captcha
